import { z } from 'zod';
import { isValidEmail } from '../email.js';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Tedarikçinin şirket profili ve banka hesapları (ADR 0114; sahip isteği 2026-10-23: "tedarikçinin
 * carisi ve tedarikçi hesabında ödeme bilgileri fatura adresi vs girme ekranları"). Profil
 * tedarikçinin kendi verisidir (control_db); banka hesabının her eklenmesi ya da değişmesi
 * değiştirilemez bir sürümdür ve bağlı her armatör şirkette dört gözle incelenir. Şemalar portal,
 * sunucu ve ofis arayüzünde ortaktır; hata iletileri koddur.
 */

// --- IBAN ve SWIFT/BIC ---

/**
 * IBAN kullanan ülkelerin IBAN uzunluğu (SWIFT IBAN Registry). Listede olmayan ülke (ör. ABD,
 * Singapur, Çin) IBAN kullanmaz: hesap numarası + SWIFT/BIC girilir.
 */
export const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BI: 27,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DJ: 27,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FK: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IQ: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  LY: 25,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MN: 20,
  MR: 27,
  MT: 31,
  MU: 30,
  NI: 28,
  NL: 18,
  NO: 15,
  OM: 23,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  RU: 33,
  SA: 24,
  SC: 31,
  SD: 18,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  SO: 23,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
  YE: 30,
};

/** Boşluk ve tire atılır, büyük harfe çevrilir (kullanıcı IBAN'ı gruplu yapıştırır). */
export function normalizeIban(value: string): string {
  return value.replace(/[\s-]+/g, '').toUpperCase();
}

/** Gösterim: dörtlü gruplar (ISO 13616 "print format"). */
export function formatIban(value: string): string {
  return normalizeIban(value).replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * ISO 13616 doğrulaması: ülke kodu, ülkenin uzunluğu ve mod-97 = 1 (ISO 7064). İlk dört karakter
 * sona alınır, harfler sayıya çevrilir (A=10 … Z=35), kalan parça parça hesaplanır.
 */
export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (IBAN_LENGTHS[iban.slice(0, 2)] !== iban.length) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const digits = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** SWIFT/BIC (ISO 9362): banka (4 harf), ülke (2 harf), yer (2), isteğe bağlı şube (3). */
export function isValidSwift(value: string): boolean {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.trim().toUpperCase());
}

/** Listede ve bildirimde yalnız son 4 hane (ADR 0114 §6). */
export function maskAccount(value: string): string {
  return `•••• ${normalizeIban(value).slice(-4)}`;
}

// --- Şemalar ---

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (min: number, max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').min(min, 'TOO_SHORT').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const country = z
  .string(typed('INVALID_COUNTRY'))
  .transform((value) => value.trim().toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}$/, 'INVALID_COUNTRY'));

/** Boş bırakılabilen, normalize edilip doğrulanan kod (IBAN, SWIFT, hesap no). */
const optionalCode = (
  normalize: (value: string) => string,
  valid: RegExp | ((v: string) => boolean),
  code: string,
) =>
  z
    .string(typed(code))
    .nullish()
    .transform((value, ctx) => {
      if (value === null || value === undefined) return null;
      const normalized = normalize(value);
      if (normalized === '') return null;
      const ok = typeof valid === 'function' ? valid(normalized) : valid.test(normalized);
      if (!ok) {
        ctx.issues.push({ code: 'custom', message: code, input: value });
        return z.NEVER;
      }
      return normalized;
    });

/**
 * Banka hesabı (ADR 0114 §3): IBAN kullanan ülkede IBAN (SWIFT isteğe bağlı), kullanmayanda hesap
 * numarası + SWIFT/BIC. İkisi birden verilmez: ödeme hangi numaraya gideceği belirsiz kalmaz.
 * Para birimi başına bir varsayılan hesap (sunucu korur).
 */
export const supplierBankAccountSchema = z
  .strictObject({
    holderName: text(2, 140),
    bankName: text(2, 140),
    iban: optionalCode(normalizeIban, isValidIban, 'INVALID_IBAN'),
    accountNumber: optionalCode(
      (value) => value.replace(/[\s-]+/g, '').toUpperCase(),
      /^[A-Z0-9]{4,34}$/,
      'INVALID_ACCOUNT_NUMBER',
    ),
    swiftBic: optionalCode((value) => value.trim().toUpperCase(), isValidSwift, 'INVALID_SWIFT'),
    currency: z
      .string(typed('INVALID_CURRENCY'))
      .transform((value) => value.trim().toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{3}$/, 'INVALID_CURRENCY')),
    isDefault: z.boolean(typed('INVALID_BOOLEAN')).default(false),
  })
  .superRefine((value, ctx) => {
    if (value.iban === null && value.accountNumber === null) {
      ctx.addIssue({ code: 'custom', message: 'ACCOUNT_REQUIRED', path: ['iban'] });
    } else if (value.iban !== null && value.accountNumber !== null) {
      ctx.addIssue({ code: 'custom', message: 'IBAN_OR_ACCOUNT', path: ['accountNumber'] });
    } else if (value.iban === null && value.swiftBic === null) {
      ctx.addIssue({ code: 'custom', message: 'SWIFT_REQUIRED', path: ['swiftBic'] });
    }
  });
export type SupplierBankAccountInput = z.output<typeof supplierBankAccountSchema>;

export function parseSupplierBankAccount(input: unknown): ParseResult<SupplierBankAccountInput> {
  const result = supplierBankAccountSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Ödemeyi belirleyen alanlar: doğrulama ve onay bunların özetine bağlıdır (ADR 0114 §4). */
export function bankPaymentDetails(account: SupplierBankAccountInput) {
  return {
    holderName: account.holderName,
    bankName: account.bankName,
    iban: account.iban,
    accountNumber: account.accountNumber,
    swiftBic: account.swiftBic,
    currency: account.currency,
  };
}

const addressSchema = z.strictObject({
  line1: text(2, 200),
  line2: optionalText(200),
  city: text(1, 100),
  postalCode: optionalText(20),
  region: optionalText(100),
  country,
});
export type SupplierAddress = z.output<typeof addressSchema>;

const contactSchema = z.strictObject({
  name: text(2, 120),
  role: text(2, 80),
  email: z
    .string(typed('INVALID_EMAIL'))
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.string().max(320, 'TOO_LONG'))
    .refine(isValidEmail, 'INVALID_EMAIL'),
  phone: optionalText(40),
});
export type SupplierContact = z.output<typeof contactSchema>;

/** Yetkili sayısının üst sınırı. */
export const MAX_SUPPLIER_CONTACTS = 10;

/**
 * Şirket profili (ADR 0114 §2): yasal ünvan, vergi bilgisi, fatura adresi, isteğe bağlı sevk
 * adresi, yetkililer. Türkiye'de vergi dairesi zorunlu, vergi no 10 hane (VKN) ya da 11 hane
 * (TCKN; şahıs işletmesi) [VUK 8, 3100 sayılı Kanun e-fatura alanları].
 */
export const supplierCompanyDetailsSchema = z
  .strictObject({
    legalName: text(2, 200),
    taxId: z
      .string(typed('INVALID_TAX_ID'))
      .transform(collapse)
      .pipe(z.string().regex(/^[A-Za-z0-9][A-Za-z0-9 .\-/]{1,39}$/, 'INVALID_TAX_ID')),
    taxOffice: optionalText(120),
    taxCountry: country,
    billingAddress: addressSchema,
    shippingAddress: addressSchema.nullish().transform((value) => value ?? null),
    contacts: z
      .array(contactSchema, typed('INVALID_OPTION'))
      .min(1, 'REQUIRED')
      .max(MAX_SUPPLIER_CONTACTS, 'TOO_MANY'),
  })
  .superRefine((value, ctx) => {
    if (value.taxCountry !== 'TR') return;
    if (!/^\d{10,11}$/.test(value.taxId)) {
      ctx.addIssue({ code: 'custom', message: 'INVALID_TAX_ID', path: ['taxId'] });
    }
    if (value.taxOffice === null) {
      ctx.addIssue({ code: 'custom', message: 'TAX_OFFICE_REQUIRED', path: ['taxOffice'] });
    }
  });
export type SupplierCompanyDetails = z.output<typeof supplierCompanyDetailsSchema>;

export function parseSupplierCompanyDetails(input: unknown): ParseResult<SupplierCompanyDetails> {
  const result = supplierCompanyDetailsSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

// --- Dört göz incelemesi ---

/**
 * Ofisin banka sürümü olayları (ADR 0114 §4): bir ofis kullanıcısı tedarikçiyi kayıtlı numaradan
 * arayıp teyit eder ve notunu yazar (`verified`), FARKLI bir kullanıcı onaylar (`approved`);
 * herhangi biri gerekçeyle reddeder (`rejected`). Onay ve ret son durumdur.
 */
export const BANK_REVIEW_ACTIONS = ['verified', 'approved', 'rejected'] as const;
export type BankReviewAction = (typeof BANK_REVIEW_ACTIONS)[number];
export type BankReviewStatus = 'pending' | BankReviewAction;

export interface BankReviewEvent {
  action: BankReviewAction;
  userId: string;
  at: string;
}

/** Doğrulama notunun (geri arama teyidi) ve ret gerekçesinin en kısa uzunluğu. */
export const BANK_VERIFY_NOTE_MIN = 10;
export const BANK_REJECT_NOTE_MIN = 5;
export const BANK_NOTE_MAX = 1000;

/** Olaylardan durum: ret ve onay son durumdur; yoksa doğrulama; olay yoksa bekliyor. */
export function bankReviewStatus(events: readonly BankReviewEvent[]): BankReviewStatus {
  if (events.some((event) => event.action === 'rejected')) return 'rejected';
  if (events.some((event) => event.action === 'approved')) return 'approved';
  if (events.some((event) => event.action === 'verified')) return 'verified';
  return 'pending';
}

/**
 * Sonraki olay geçerli mi; geçerliyse yeni durum. Doğrulayan onaylayamaz
 * (`SAME_APPROVER_FORBIDDEN`): ödeme yönlendirme dolandırıcılığına karşı iki kişi, iki kaynak.
 */
export function nextBankReview(
  events: readonly BankReviewEvent[],
  input: { action: BankReviewAction; userId: string; note: string | null },
): BankReviewStatus {
  const status = bankReviewStatus(events);
  const note = input.note?.trim() ?? '';
  if (note.length > BANK_NOTE_MAX) throw new DomainError('NOTE_TOO_LONG');
  switch (input.action) {
    case 'verified':
      if (status !== 'pending')
        throw new DomainError('INVALID_TRANSITION', `INVALID_TRANSITION: ${status}`);
      if (note.length < BANK_VERIFY_NOTE_MIN) throw new DomainError('NOTE_REQUIRED');
      return 'verified';
    case 'approved': {
      if (status !== 'verified')
        throw new DomainError('INVALID_TRANSITION', `INVALID_TRANSITION: ${status}`);
      const verifier = events.find((event) => event.action === 'verified');
      if (verifier?.userId === input.userId) throw new DomainError('SAME_APPROVER_FORBIDDEN');
      return 'approved';
    }
    case 'rejected':
      if (status !== 'pending' && status !== 'verified') {
        throw new DomainError('INVALID_TRANSITION', `INVALID_TRANSITION: ${status}`);
      }
      if (note.length < BANK_REJECT_NOTE_MIN) throw new DomainError('NOTE_REQUIRED');
      return 'rejected';
  }
}
